//src/pages/attendance/LogsTable.jsx
import { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { getAttendanceByDoctorId, getAttendanceByStaffId } from "../../services/attendanceService";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  styled,
  useTheme,
  TextField,
  Menu,
  MenuItem,
  Stack,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import dayjs from "dayjs";
import AddNewInvoice from "./ApplyLeave";
import EditAttendanceModal from "./EditAttendanceModal"; // Import the new modal
import ApplyLeaveEditModal from "./ApplyLeaveEditModal"; // Import the new ApplyLeaveEditModal
import { useSelector } from 'react-redux'; // Import useSelector
import { getDoctorByUserId } from "../../services/doctorService"; // Import the service
import { getStaffByUserId } from "../../services/StaffService"; // Import the service
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import { getOrganizationName } from "../../utils/organizationUtils";
import { downloadStyledTableExcel } from "../../utils/patientTableExcelExport";
import {
  TABLE_ACTIONS_ROW_SX,
  TABLE_ADD_BUTTON_SX,
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../utils/pageLayoutStyles";

const columns = [
  // { id: "attendanceId", label: "Attendance Id" },
  { id: "attendanceDate", label: "Date" },
  { id: "checkInTime", label: "Check In Time" },
  { id: "checkOutTime", label: "Check Out Time" },
  { id: "status", label: "Status" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

export default function AttendanceTable(doctorId) {
  const theme = useTheme();
  const [open_invoice_modal, setOpenInvoiceModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false); // State for edit modal
  const [openApplyLeaveModal, setOpenApplyLeaveModal] = useState(false); // State for apply leave modal
  const [invoice_info, setInvoiceInfo] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState({ invoice_list: true });
  const [invoice_list, setInvoiceList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null); // State for menu anchor
  const user = useSelector((state) => state.user.user); // Access user from Redux

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, []);

  const fetchInvoice = async () => {
    setFetching({ ...fetching, invoice_list: true });
    try {

      let userDetails;
      // Fetch doctor or staff details based on user.id
      if (user.roleName === "doctor" || user.roleName === "surgeon") {
        userDetails = await getDoctorByUserId(user.id); // Fetch doctor details
      } else {
        userDetails = await getStaffByUserId(user.id); // Fetch staff details
      }

      let data;
      if (user.roleName === "doctor" || user.roleName === "surgeon") {
        data = await getAttendanceByDoctorId(userDetails.id); // Use fetched doctor ID
      } else {
        data = await getAttendanceByStaffId(userDetails.id); // Use fetched staff ID
      }
      // Sort data in descending order by attendanceId
      data.sort((a, b) => b.attendanceId - a.attendanceId);

      setInvoiceList(data);
      setFilteredList(data);
    } catch (error) {
      console.log(error);
    }
    setFetching({ ...fetching, invoice_list: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    const filtered = invoice_list.filter((row) => {
      const attendanceDate = new Date(row.attendanceDate);
      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(toDate) : null;
      const matchesDate = (!from || attendanceDate >= new Date(from.setHours(0,0,0,0))) && (!to || attendanceDate <= new Date(to.setHours(23,59,59,999)));
      const matchesStatus = statusFilter ? String(row.status || "") === statusFilter : true;
      const matchesSearch = searchQuery.trim() ? [row.attendanceDate, row.checkInTime, row.checkOutTime, row.status]
        .filter(Boolean).map(v => String(v).toLowerCase()).some(v => v.includes(searchQuery.toLowerCase())) : true;
      return matchesDate && matchesStatus && matchesSearch;
    });
    setFilteredList(filtered);
    setPage(0);
  }, [invoice_list, fromDate, toDate, statusFilter, searchQuery]);

  const clearFilters = () => {
    setFromDate(""); // Clear the from date filter
    setToDate(""); // Clear the to date filter
    setStatusFilter("");
    setSearchQuery("");
    setFilteredList(invoice_list); // Reset to original data
    setPage(0); // Reset pagination
  };

  const handleExport = async () => {
    const headers = ["Date", "Check In", "Check Out", "Status"];
    const tableRows = filteredList.map((r) => [
      dayjs(r.attendanceDate).format("YYYY-MM-DD"),
      r.checkInTime || "",
      r.checkOutTime || "",
      r.status || "",
    ]);

    await downloadStyledTableExcel({
      filename: `attendance_logs_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Attendance Logs",
      title: "Attendance Logs",
      organizationName: getOrganizationName(user),
      tableHeaders: headers,
      tableRows,
    });
  };

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setInvoiceInfo(row); // Set the selected row data
    console.log("Selected row data: ", row); // Log the row data to verify
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditAttendance = () => {
    setOpenEditModal(true);
    handleMenuClose();
  };
  
  const handleApplyLeave = () => {
    setOpenApplyLeaveModal(true); // Open the ApplyLeaveEditModal
    handleMenuClose();
  };

  // console.log("attendance date: ", invoice_info.attendanceDate);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sx={TABLE_ACTIONS_ROW_SX}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={TABLE_ADD_BUTTON_SX}
          onClick={() => {
            setOpenInvoiceModal(true);
          }}
        >
          Apply Leave
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ width: "100%" }}>
          <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
            <Box sx={TABLE_SEARCH_ROW_SX}>
              <TextField
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
                <IconButton
                  size="small"
                  sx={TABLE_ICON_BUTTON_SX}
                  onClick={() => setShowFilters((v) => !v)}
                >
                  <FilterAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export to Excel">
                <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton
                  size="small"
                  sx={TABLE_ICON_BUTTON_SX}
                  onClick={async () => await fetchInvoice()}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {showFilters && (
            <Box mt={1} display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
              <TextField label="Status" size="small" value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} select sx={{ minWidth: 140 }}>
                <MenuItem value="">All</MenuItem>
                {[...new Set(invoice_list.map(r=>r.status).filter(Boolean))].map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
              <TextField label="From Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              <TextField label="To Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} />
              <Tooltip title="Clear filters">
                <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                  <ClearAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            )}
          </Box>
          <TableContainer sx={{ maxHeight: 550 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <StyledTableCell key={column.id} style={{ minWidth: column.minWidth }}>
                      {column.label}
                    </StyledTableCell>
                  ))}
                  <StyledTableCell key={"action"}>Action</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredList.length === 0 || fetching.invoice_list ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1}>
                      {fetching.invoice_list && <CircularProgress size={40} />}
                      {!fetching.invoice_list && filteredList.length === 0 && (
                        <Alert>No Attendance Records</Alert>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={row.attendanceId}>
                          {columns.map((column) => {
                            const value = row[column.id];
                            if (column.id === "attendanceDate") {
                              return (
                                <TableCell key={column.id}>
                                  {dayjs(value).format("D MMM YYYY")}
                                </TableCell>
                              );
                            } else {
                              return <TableCell key={column.id}>{value}</TableCell>;
                            }
                          })}
                          <TableCell>
                            {(row.status === "ABSENT" && (!row.checkInTime || !row.checkOutTime)) || 
                            (row.status === "ABSENT" && row.status !== "ON_LEAVE") && (
                              <IconButton
                                size="small"
                                sx={TABLE_ICON_BUTTON_SX}
                                onClick={(event) => handleMenuClick(event, row)}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={filteredList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Grid>

      <AddNewInvoice
        open={open_invoice_modal}
        onClose={async () => {
          setOpenInvoiceModal(false);
          await fetchInvoice(); // Fetch the latest data after closing the modal
          setInvoiceInfo({});
        }}
        invoice_info={invoice_info}
      />

      <EditAttendanceModal
        open={openEditModal}
        onClose={async () => {
          setOpenEditModal(false);
          await fetchInvoice(); // Fetch the latest data after editing
        }}
        attendanceInfo={invoice_info}
      />

      <ApplyLeaveEditModal
        open={openApplyLeaveModal}
        onClose={async () => {
          setOpenApplyLeaveModal(false);
          await fetchInvoice(); // Fetch the latest data after editing
        }}
        attendanceInfo={invoice_info} // Pass the entire attendanceInfo object
        // attendanceDate={invoice_info.attendanceDate} // Pass the attendance date directly
        // attendanceDate={dayjs(invoice_info.attendanceDate).format("YYYY-MM-DD")} // Pass the attendance date in correct format
      />

      {/* Menu for Edit Attendance and Apply Leave */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditAttendance}>Edit Attendance</MenuItem>
        <MenuItem onClick={handleApplyLeave}>Apply Leave</MenuItem>
      </Menu>

    </Grid>
  );
}
